import { GraphQLError } from '@pylot-data/graphqlFetch'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'
import { graphqlFetch, GraphqlFetchOutput } from '@pylot-data/graphqlFetch'
import type { Cart, CartItemInterface, Maybe } from '@pylot-data/pylotschema'
import { updateCartItemsMutation } from './graphql/updateCartItemsMutation'
import { CartData, useCart } from './use-cart'
import { getIsSignedIn } from './utils/getIsSignedIn'
import { useUI } from '@corratech/pylot-ui/context'

export type CartItemUpdateInput = {
    cart_item_uid: string
    quantity: number
}

export type UpdateCartItemsInput = {
    input: {
        cart_id: string
        cart_items: CartItemUpdateInput[]
    }
    isSignedIn: boolean
}

export type UpdateCartItemsOutput = {
    updateCartItems: {
        cart: Cart
    }
    errors?: GraphQLError[]
}

const getOptimisticData = (
    cartData: CartData,
    cartItems: CartItemUpdateInput[]
): CartData => {
    const items = [...(cartData?.data?.cart?.items || [])]
    const updatedItems = items!.reduce<Maybe<CartItemInterface[]>>(
        (newItems, item) => {
            const correspondingItemToUpdate = cartItems?.find(
                (updatedItem) => updatedItem.cart_item_uid === item?.uid!
            )

            if (!correspondingItemToUpdate) {
                // @ts-ignore
                return [...newItems, item]
            }

            if (correspondingItemToUpdate.quantity === 0) {
                return newItems
            }

            return [
                ...newItems,
                {
                    ...item,
                    quantity: correspondingItemToUpdate.quantity
                }
            ]
        },
        []
    )

    return {
        data: {
            cart: {
                ...cartData.data.cart,
                items: updatedItems
            },
            isOptimistic: true
        }
    }
}

const formatData = (
    output: GraphqlFetchOutput<UpdateCartItemsOutput>
): CartData => {
    return {
        data: {
            cart: output.data.updateCartItems?.cart ?? null
        },
        errors: output.errors
    }
}

export type UpdateCartItems = (
    cartItems: CartItemUpdateInput[]
) => Promise<CartData | undefined>

export interface UseUpdateCartItemsReturn {
    isLoading: boolean
    updateCartItems: UpdateCartItems
    errorMessage: string
}

export const useUpdateCartItems = (): UseUpdateCartItemsReturn => {
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const { locale } = useRouter()
    const { data, mutate } = useCart()
    const { setMinicartLoading } = useUI()

    // Use `useCallback` to make sure that `updateCart` stays the same.
    // eslint-disable-next-line
    const updateCart = useCallback(
        async (cartItems) => {
            // update cart items, await for the response
            setErrorMessage('')
            const response = await graphqlFetch<
                UpdateCartItemsInput,
                UpdateCartItemsOutput
            >({
                query: updateCartItemsMutation,
                variables: {
                    input: {
                        cart_id: String(data!.data.cart.id!),
                        cart_items: cartItems
                    },
                    isSignedIn: getIsSignedIn()
                },
                locale
            })
            setMinicartLoading(false)
            // if response contains errors
            if (response?.errors?.length) {
                // Leave the old cart data untouched, but indicate, that error has occurred.
                // And trigger the revalidation to get latest cart data with no errors
                setErrorMessage(response.errors[0].message)
                setIsLoading(false)
                await mutate({
                    data: data!.data,
                    errors: response.errors
                })
                return false
            }

            // Otherwise update internal cache with the fresh data after updating items.
            // In this case, revalidation is not necessary, as data is fresh.
            setErrorMessage('')
            await mutate(formatData(response), false)
            setIsLoading(false)
            return true
        },
        [data?.data?.cart?.id, mutate, locale, setMinicartLoading]
    )

    return {
        isLoading,
        updateCartItems: useCallback(
            async (cartItems: CartItemUpdateInput[]) => {
                // mutate the internal cache of useSWR with the optimistic UI data
                // (which is expected data after user modification).
                setErrorMessage('')
                setMinicartLoading(true)
                setIsLoading(true)
                const success = await updateCart(cartItems)
                if (!success) {
                    setIsLoading(false)
                    return undefined
                }
                const optimisticData = getOptimisticData(data!, cartItems)

                // trigger network request with actual mutation and update cart data
                return mutate(optimisticData, false)
            },
            [data, mutate, updateCart]
        ),
        errorMessage
    }
}
